import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";

class CurrencyExchangeService {
  constructor() {
    this.apiKey = "f7dde5cdeb39cd8ed44008e8e924da592ff5a0b6";
    this.baseUrl = "https://api.getgeoapi.com/v2/currency/convert";
    this.exchangeRatesCollection = "exchangeRates";
    this.supportedCurrencies = ["USD", "EUR", "CAD", "TRY"];
    this.baseCurrency = "TRY"; // Turkish Lira as base
  }

  /**
   * Fetch current exchange rates from API
   */
  async fetchExchangeRates() {
    try {
      const rates = {};

      // Fetch rates for each supported currency against TRY
      for (const currency of this.supportedCurrencies) {
        if (currency === this.baseCurrency) {
          rates[currency] = 1; // Base currency rate is always 1
          continue;
        }

        const url = `${this.baseUrl}?api_key=${this.apiKey}&from=${this.baseCurrency}&to=${currency}&amount=1&format=json`;

        try {
          const response = await fetch(url);
          const data = await response.json();

          if (data.status === "success" && data.rates && data.rates[currency]) {
            rates[currency] = parseFloat(data.rates[currency].rate);
          } else {
            console.warn(`Failed to fetch rate for ${currency}:`, data);
            // Use fallback rates if API fails
            rates[currency] = this.getFallbackRate(currency);
          }
        } catch (error) {
          console.error(`Error fetching rate for ${currency}:`, error);
          rates[currency] = this.getFallbackRate(currency);
        }

        // Add small delay to avoid rate limiting
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      return {
        success: true,
        rates,
        updatedAt: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error fetching exchange rates:", error);
      return {
        success: false,
        error: error.message,
        rates: this.getFallbackRates(),
        updatedAt: new Date().toISOString(),
      };
    }
  }

  /**
   * Get fallback exchange rates (approximate values)
   */
  getFallbackRates() {
    return {
      TRY: 1,
      USD: 0.03, // Approximate: 1 TRY = 0.03 USD
      EUR: 0.028, // Approximate: 1 TRY = 0.028 EUR
      CAD: 0.042, // Approximate: 1 TRY = 0.042 CAD
    };
  }

  /**
   * Get fallback rate for a specific currency
   */
  getFallbackRate(currency) {
    const fallbackRates = this.getFallbackRates();
    return fallbackRates[currency] || 1;
  }

  /**
   * Save exchange rates to Firestore
   */
  async saveExchangeRates(rates, updatedAt) {
    try {
      const ratesDoc = {
        rates,
        updatedAt,
        baseCurrency: this.baseCurrency,
        supportedCurrencies: this.supportedCurrencies,
      };

      await setDoc(doc(db, this.exchangeRatesCollection, "current"), ratesDoc);

      return { success: true };
    } catch (error) {
      console.error("Error saving exchange rates:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get exchange rates from Firestore
   */
  async getStoredExchangeRates() {
    try {
      const ratesDoc = await getDoc(
        doc(db, this.exchangeRatesCollection, "current")
      );

      if (ratesDoc.exists()) {
        return {
          success: true,
          data: ratesDoc.data(),
        };
      } else {
        return {
          success: false,
          error: "No stored exchange rates found",
        };
      }
    } catch (error) {
      console.error("Error getting stored exchange rates:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Check if exchange rates need updating (older than 7 days)
   */
  isUpdateNeeded(lastUpdated) {
    if (!lastUpdated) return true;

    const lastUpdateDate = new Date(lastUpdated);
    const now = new Date();
    const daysDiff = (now - lastUpdateDate) / (1000 * 60 * 60 * 24);

    return daysDiff >= 7;
  }

  /**
   * Update exchange rates if needed
   */
  async updateExchangeRatesIfNeeded() {
    try {
      // Get stored rates
      const storedResult = await this.getStoredExchangeRates();

      let shouldUpdate = true;
      if (storedResult.success) {
        shouldUpdate = this.isUpdateNeeded(storedResult.data.updatedAt);
      }

      if (shouldUpdate) {
        console.log("Updating exchange rates...");
        const fetchResult = await this.fetchExchangeRates();

        if (fetchResult.success) {
          await this.saveExchangeRates(
            fetchResult.rates,
            fetchResult.updatedAt
          );
          return {
            success: true,
            updated: true,
            rates: fetchResult.rates,
            updatedAt: fetchResult.updatedAt,
          };
        } else {
          // Use stored rates if fetch fails
          return {
            success: true,
            updated: false,
            rates: storedResult.success
              ? storedResult.data.rates
              : this.getFallbackRates(),
            updatedAt: storedResult.success
              ? storedResult.data.updatedAt
              : new Date().toISOString(),
            error: fetchResult.error,
          };
        }
      } else {
        return {
          success: true,
          updated: false,
          rates: storedResult.data.rates,
          updatedAt: storedResult.data.updatedAt,
        };
      }
    } catch (error) {
      console.error("Error in updateExchangeRatesIfNeeded:", error);
      return {
        success: false,
        error: error.message,
        rates: this.getFallbackRates(),
        updatedAt: new Date().toISOString(),
      };
    }
  }

  /**
   * Force update exchange rates (for manual update)
   */
  async forceUpdateExchangeRates() {
    try {
      console.log("Force updating exchange rates...");
      const fetchResult = await this.fetchExchangeRates();

      if (fetchResult.success) {
        await this.saveExchangeRates(fetchResult.rates, fetchResult.updatedAt);
      }

      return fetchResult;
    } catch (error) {
      console.error("Error in forceUpdateExchangeRates:", error);
      return {
        success: false,
        error: error.message,
        rates: this.getFallbackRates(),
        updatedAt: new Date().toISOString(),
      };
    }
  }

  /**
   * Convert amount from base currency (TRY) to target currency
   */
  convertFromBase(amount, targetCurrency, rates) {
    if (!amount || !targetCurrency || !rates) return 0;
    if (targetCurrency === this.baseCurrency) return amount;

    const rate = rates[targetCurrency];
    return rate ? amount * rate : amount;
  }

  /**
   * Convert amount from any currency to base currency (TRY)
   */
  convertToBase(amount, fromCurrency, rates) {
    if (!amount || !fromCurrency || !rates) return 0;
    if (fromCurrency === this.baseCurrency) return amount;

    const rate = rates[fromCurrency];
    return rate ? amount / rate : amount;
  }

  /**
   * Convert amount between any two currencies
   */
  convertBetweenCurrencies(amount, fromCurrency, toCurrency, rates) {
    if (!amount || !fromCurrency || !toCurrency || !rates) return 0;
    if (fromCurrency === toCurrency) return amount;

    // Convert to base currency first, then to target currency
    const baseAmount = this.convertToBase(amount, fromCurrency, rates);
    return this.convertFromBase(baseAmount, toCurrency, rates);
  }

  /**
   * Get currency symbol
   */
  getCurrencySymbol(currency) {
    const symbols = {
      TRY: "₺",
      USD: "$",
      EUR: "€",
      CAD: "C$",
    };
    return symbols[currency] || currency;
  }

  /**
   * Get currency name
   */
  getCurrencyName(currency) {
    const names = {
      TRY: "Turkish Lira",
      USD: "US Dollar",
      EUR: "Euro",
      CAD: "Canadian Dollar",
    };
    return names[currency] || currency;
  }

  /**
   * Format amount with currency symbol
   */
  formatCurrencyAmount(amount, currency) {
    if (!amount || !currency) return "0";

    const symbol = this.getCurrencySymbol(currency);

    // Use appropriate locale for formatting
    const locales = {
      TRY: "tr-TR",
      USD: "en-US",
      EUR: "de-DE",
      CAD: "en-CA",
    };

    const locale = locales[currency] || "en-US";

    try {
      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);

      return `${symbol}${formatted}`;
    } catch (error) {
      console.warn("Error formatting currency:", error);
      return `${symbol}${amount.toFixed(2)}`;
    }
  }
}

export default new CurrencyExchangeService();
